import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "react-query";
import { useAppDispatch } from "../../hooks/hooks";
import { useGetFormationAreasQuery } from "../../hooks/useGetFormationAreasQuery";
import { updateFormationArea } from "../../services/school/formationArea";
import { Key_FormationAreas } from "../../utils/constants";
import toast from "react-hot-toast";
import { routes } from "../../routes";
import { Pencil1Icon, TrashIcon } from "@radix-ui/react-icons";
import { Lists } from "../../components/common/Lists";
import TableComponent from "../../components/common/Tables";
import { ConfirmAlertDialog } from "../../components/common/ConfirmAlertDialog";
import { FormationAreaType } from "../../types/FormationAreaType";
import { setFormationAreaData } from "../../redux/slices/school/formationArea";

const columnNamesMap: { [key: string]: string } = {
    description: "Nome",
};

export const FormationAreas = () => {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [itemToBeDeleted, setItemToBeDeleted] = useState<FormationAreaType | null>(null);

    const queryClient = useQueryClient();
    const dispatch = useAppDispatch();
    const { data, status } = useGetFormationAreasQuery({ PageNumber: currentPage, PageSize: 10 });

    const mutationUpdate = useMutation(updateFormationArea, {
        onSuccess: () => {
            queryClient.invalidateQueries(Key_FormationAreas);
            toast.success("Área de formação removida com sucesso");
        },
        onError: () => {
            toast.error("Erro ao remover a área de formação.");
        }
    });

    const handleCreate = () => {
        navigate(routes.createFormationArea);
    };

    const handleEdit = (area: FormationAreaType) => {
        dispatch(setFormationAreaData(area));
        navigate(routes.updateFormationArea);
    };

    const handleDelete = (area: FormationAreaType) => {
        area.status = false;
        mutationUpdate.mutateAsync(area);
    };

    const openDeleteDialog = (area: FormationAreaType) => {
        setItemToBeDeleted(area);
    };

    const confirmDelete = () => {
        if (itemToBeDeleted) {
            handleDelete(itemToBeDeleted);
        }
    };

    const options = data?.items.map((area) => [
        {
            label: "Editar",
            onClick: () => handleEdit(area),
            icon: <Pencil1Icon />
        },
        {
            label: "Excluir",
            onClick: () => openDeleteDialog(area),
            icon: <TrashIcon />
        },
    ]) || [];

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    return (
        <>
            <Lists
                status={status}
                TableComponent={() => (
                    <TableComponent
                        columns={Object.keys(columnNamesMap)}
                        items={data?.items}
                        options={options}
                        columnNamesMap={columnNamesMap}
                        totalPages={data?.totalPages || 1}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                    />
                )}
                handleClickCreate={handleCreate}
            />
            <ConfirmAlertDialog
                open={!!itemToBeDeleted}
                onOpenChange={(open) => !open && setItemToBeDeleted(null)}
                title="Esta operação é irreversível"
                item={itemToBeDeleted}
                onConfirm={confirmDelete}
                onCancel={() => setItemToBeDeleted(null)}
            />
        </>
    );
};
